/**
 * ออกเอกสารติดตาม — the pure half of the follow-up letter: which claim lines go on it, how
 * they are grouped, and what each cell says. No React, no fetch, so `node --test` can hold
 * it to account (followUpDoc.test.mjs).
 *
 * ⚠️ OUTSTANDING MEANS `pending` AND NOTHING ELSE — the same rule as Reimbursement.jsx#bOwed.
 * `partial` is closed at the settled amount and `rejected` is a human write-off; putting
 * either on a letter asks the payer for money the clinic has already stopped claiming.
 *
 * ⚠️ ONE SECTION PER BRANCH, NEVER ONE MERGED TABLE. The 12 branches sit under 5 different
 * juristic entities (DocumentGenerator.jsx), and the creditor on a demand is the company
 * that treated — so each branch prints under its OWN company name, address and tax id.
 * The payers settle per branch anyway (AXA and BlueVenture send one file per branch).
 */

const CLAIM_STATES = new Set(['pending', 'received', 'partial', 'rejected'])
const num = (v) => { const n = Number(String(v ?? '').replace(/[, ฿]/g, '')); return Number.isFinite(n) ? n : 0 }

export const lineStatus = (b) => {
  const s = String(b?.advance_status ?? '').toLowerCase()
  return CLAIM_STATES.has(s) ? s : 'pending'
}
export const isOutstanding = (b) => b?.active !== false && lineStatus(b) === 'pending' && num(b?.priceadvance) > 0

const stripStars = (s) => String(s ?? '').replace(/^\**|\**$/g, '').trim()

/** api 640's `treatment_date` is the clinic's calendar day; `date` (epoch ms) is the fallback. */
export function lineDay(b) {
  const td = String(b?.treatment_date ?? '').slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(td)) return td
  const ms = Number(b?.date)
  if (!ms) return ''
  // epoch ms → Bangkok calendar day, independent of the viewer's machine
  return new Date(ms + 7 * 3600 * 1000).toISOString().slice(0, 10)
}

/** Whole days from `day` to `today` (both YYYY-MM-DD). */
export function daysSince(day, today) {
  if (!day || !today) return null
  const a = Date.UTC(...day.split('-').map((n, i) => Number(n) - (i === 1 ? 1 : 0)))
  const b = Date.UTC(...today.split('-').map((n, i) => Number(n) - (i === 1 ? 1 : 0)))
  return Math.round((b - a) / 86400000)
}

const TH_MON = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
const TH_MON_FULL = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']
/** '2026-07-01' → '1 ก.ค. 69' (table) or '1 กรกฎาคม 2569' (letter). Buddhist era. */
export function thDay(iso, { full = false } = {}) {
  const [y, m, d] = String(iso || '').split('-').map(Number)
  if (!y || !m || !d) return '—'
  const be = y + 543
  return full ? `${d} ${TH_MON_FULL[m - 1]} ${be}` : `${d} ${TH_MON[m - 1]} ${String(be).slice(-2)}`
}

/** ทพ./ทพญ. + ชื่อ นามสกุล, from the doctor record. Never a nickname — this goes to a payer. */
export function doctorFullName(d) {
  if (!d) return ''
  const name = [stripStars(d.name), stripStars(d.surname)].filter(Boolean).join(' ')
  const prefix = String(d.prefix ?? '').trim()
  if (!name) return stripStars(d.name_eng || d.code || '')
  return prefix ? `${prefix}${name}` : name
}

/**
 * What was actually done on the visit — the treatment's own priced lines. The claim line
 * itself is the reversal ("เบิกคืนบริษัทกรุงไทย AXA", negative price), which tells a payer
 * nothing, and the ฿0 "ค่าบริการเรียกเก็บจากบริษัทประกัน" marker is not a procedure either.
 */
export function procedureNames(billingLines) {
  const out = []
  for (const b of billingLines || []) {
    if (b?.active === false) continue
    if (num(b?.price) <= 0) continue
    if (String(b?.advancePay ?? '').trim()) continue
    const bt = b?.billing_type
    const name = String((bt && typeof bt === 'object' ? bt.name : '') || b?.billing_type_name || b?.detail || '').trim()
    if (name && !out.includes(name)) out.push(name)
  }
  return out
}

/** One printable row per outstanding claim line. `doctors` and `procedures` are Maps. */
export function buildRows(lines, { doctors = new Map(), procedures = new Map(), today } = {}) {
  return (lines || []).filter(isOutstanding).map((b) => {
    const day = lineDay(b)
    const procs = procedures.get(String(b.treatment_id))
    return {
      id: String(b.id),
      treatmentId: String(b.treatment_id ?? ''),
      branchId: String(b.branch_id ?? ''),
      day,
      age: daysSince(day, today),
      hn: String(b.HN ?? b.hn ?? '').trim(),
      patient: stripStars(b.fullname),
      doctor: doctorFullName(doctors.get(String(b.doctor_id))),
      items: procs && procs.length ? procs.join(', ') : '',
      claimRef: String(b.claimRef ?? b.claim_ref ?? '').trim(),
      amount: num(b.priceadvance),
    }
  })
}

/** Rows grouped by branch, oldest visit first inside each, branches in a stable order. */
export function groupByBranch(rows, branchOrder = []) {
  const m = new Map()
  for (const r of rows) {
    if (!m.has(r.branchId)) m.set(r.branchId, [])
    m.get(r.branchId).push(r)
  }
  const rank = (id) => { const i = branchOrder.indexOf(id); return i < 0 ? 1e9 : i }
  return [...m.entries()]
    .sort((a, b) => rank(a[0]) - rank(b[0]) || Number(a[0]) - Number(b[0]))
    .map(([branchId, rs]) => ({
      branchId,
      rows: rs.slice().sort((x, y) => x.day.localeCompare(y.day) || x.claimRef.localeCompare(y.claimRef)),
      total: rs.reduce((s, r) => s + r.amount, 0),
    }))
}

/**
 * The letter's grouping (Ram, 2026-10-14): ONE letter under บริษัท สไมล์ซีซันส์ จำกัด,
 * sections by เดือน/ปี of the visit, oldest first. The branch moves into a column — the
 * payer still needs it, because they settle per branch. (The on-screen picker keeps its
 * branch grouping; only the letter is by month.)
 */
export function groupByMonth(rows) {
  const m = new Map()
  for (const r of rows) {
    const k = String(r.day || '').slice(0, 7) || '—'
    if (!m.has(k)) m.set(k, [])
    m.get(k).push(r)
  }
  return [...m.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, rs]) => {
      const [y, mo] = month.split('-').map(Number)
      return {
        month,
        label: y && mo ? `${TH_MON_FULL[mo - 1]} ${y + 543}` : 'ไม่ระบุวันที่',
        rows: rs.slice().sort((x, z) => x.day.localeCompare(z.day) || x.claimRef.localeCompare(z.claimRef)),
        total: rs.reduce((s, r) => s + r.amount, 0),
      }
    })
}

/** '0812345678' → '081-234-5678'; a 9-digit landline → '02-114-3274'; anything else as typed. */
export function fmtPhone(p) {
  const d = String(p ?? '').replace(/\D/g, '')
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`
  if (d.length === 9 && d.startsWith('02')) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`
  return String(p ?? '').trim()
}
